import {type FunctionalComponent, h} from '@stencil/core';
import type { SearchApiDocumentMySE } from '../../../../../api/SearchApi';
import { SearchTableActionType, type SearchTableColumnConfiguration } from '../SearchTableConfiguration';
import { SearchTableRowType } from '../SearchTableRowType';
import {buildUrl, downloadFile, sanitizeHTML} from '../utils';
import pdfIcon from './assets/pdfIcon.svg';
import Icon_Documents_default from '../../../../../assets/svg/doctypes-icons/Icon_Documents_default.svg';

export interface SearchTableColumnProps{
    doc: SearchApiDocumentMySE,
    columns: SearchTableColumnConfiguration[],
    labelProvider: CallableFunction,
    tableDeletedActionClicked: CallableFunction
}

export const SearchTableColumn:FunctionalComponent<SearchTableColumnProps> = (props) => {
    const labelProvider = props.labelProvider;
    const columns = props.columns;
    const doc = props.doc;
    const tableDeletedActionClicked = props.tableDeletedActionClicked;
    return <tr class="search-table-row-group">
      {columns.map(col => {
          return column(doc, col, labelProvider, tableDeletedActionClicked);
      })}
    </tr>;
};

function column(doc: SearchApiDocumentMySE, column: SearchTableColumnConfiguration, labelProvider, tableDeletedActionClicked){
    let value = '';
    if (doc.hasOwnProperty(column.fieldName)) {
      value = doc[column.fieldName];
    }

    switch (column.fieldType.toLowerCase()) {
    case SearchTableRowType.Link:

      if (doc.pdf && column.fieldName === 'pdf') {
        return (
          <td class={'search-table-column-item search-table-link-file' + (column.icon ? ' icon-text' : '')}
            style={{'width': column.width, paddingLeft: '25px'}} role="cell">
            <div style={{'display':'inline-flex', 'alignItems': 'center'}}>
              <div style={{'width': '20px', 'height': '20px'}} innerHTML={pdfIcon}></div>
              <a class="search-table-link-text" href={doc.pdf.url} innerHTML={doc.pdf.title} target="_blank"></a>
            </div>
          </td>
        );
      }
      if (doc.csaf && column.fieldName === 'csaf'){
        return (
          <td class={'search-table-column-item search-table-link-file' + (column.icon ? ' icon-text' : '')}
            style={{'width': column.width, paddingLeft: '25px'}} role="cell">
            <div style={{'display': 'inline-flex', 'alignItems': 'center'}}>
              <div style={{'width': '20px', 'height': '20px'}} innerHTML={Icon_Documents_default}></div>
              <a class="search-table-link-text" href={doc.csaf.url} innerHTML={doc.csaf.title} target="_blank"></a>
            </div>
          </td>
        );
      }
      const linkUrl = buildUrl(column.url, doc);
      return (<td class="search-table-column-item" role="cell" style={{ 'width': column.width }}><span class={column.paddingLeft ? 'none' : 'search-table-column-item-content' } style={{ 'padding-left': column.paddingLeft ? column.paddingLeft : 'none' }}><a class="search-table-link-text" href={linkUrl} innerHTML={value}></a></span></td>);

    case SearchTableRowType.File:

      const fileUrl = buildUrl(column.url, doc);
      if (column.quotePdfConfiguration) {
        return(<td class={'search-table-column-item search-table-link-file' + (column.icon ? ' icon-text' : '')} style={{ 'width': column.width }} role="cell">
          <uiaas-search-quotes-list-pdf
            pdfConfiguration={column.quotePdfConfiguration}
            quoteNumber={doc['quotationNumber']}
          ></uiaas-search-quotes-list-pdf>
        </td>);
      }
      if(column.icon){
        return(<td class={'search-table-column-item' + (column.icon ? ' icon-text' : '')} style={{ 'width': column.width }} role="cell">
          <button class="search-table-link-file search-table-pdf-buttton" innerHTML={pdfIcon} onClick={() => downloadFile(fileUrl, column.fileName)}></button>
        </td>);
      }

      break;
    case SearchTableRowType.Text:

      return (<td class="search-table-column-item" style={{ 'width': column.width }} role="cell"><span class="search-table-column-item-content" innerHTML={value}></span></td>);

    case SearchTableRowType.Date:

      let displayDate = '';
      if(doc[column.fieldName]){
        const date = new Date(doc[column.fieldName]).toISOString();
        const hours = column.dateColumnWithHours ? `${labelProvider('at', 'at')} ${date.slice(11,16)}` : '';
        displayDate = `${date.slice(0,4)}/${date.slice(5,7)}/${date.slice(8,10)} ${hours}`;
      }
      return (<td class="search-table-column-item" style={{ 'width': column.width }} role="cell"><span class="search-table-column-item-content" innerHTML={displayDate}></span></td>);

    case SearchTableRowType.Status:

      return (
        <td class="search-table-column-item" style={{ 'width': column.width }} role="cell">
          <qds-tag
            text={doc[column.fieldName]}
            status={this.mapQuoteStatusToColor()}
            size="small"
          />
        </td>);

    case SearchTableRowType.Action:

      return (
        <td class="search-table-column-item icon-text" style={{ 'width': column.width }} role="cell">
          {
            column.actions?.map((action, index) => {
                if (action.actionType === SearchTableActionType.Edit) {
                  const linkUrl = buildUrl(action.fallbackUrl, doc);
                  return (
                    <span><se-icon size="small" class="search-table-action-icon"
                      //@ts-ignore DRUTY - wrong attribute or value
                      style={index > 0 ? { 'margin-left': '24px' } : ''}>action_editor</se-icon>
                    <a class="search-table-link-text" href={linkUrl}>{action.buttonText}</a>
                    </span>
                  );
                }
                if (action.actionType === SearchTableActionType.Delete) {
                  const linkUrl = buildUrl(action.fallbackUrl, doc);
                  return (
                    <span><se-icon size="small" class="search-table-action-icon"
                      //@ts-ignore DRUTY - wrong attribute or value
                      style={index > 0 ? { 'margin-left': '24px' } : ''}>action_delete</se-icon>
                    <a class="search-table-link-text" onClick={() => tableDeletedActionClicked(doc[action.idFieldName], linkUrl)}>{action.buttonText}</a>
                    </span>
                  );
                }
            })
          }
        </td>);

    case SearchTableRowType.List:

      return (<td class="search-table-column-item" style={{ 'width': column.width }} role="cell"><span class="search-table-column-item-content">
        <list-row-type value={value}></list-row-type>
      </span></td>);

    case SearchTableRowType.SanitizedText:
      const sanitizedValue = sanitizeHTML(value);
      return (<td class="search-table-column-item" style={{ 'width': column.width }} role="cell"><span class="search-table-column-item-content" innerHTML={sanitizedValue}></span></td>);

    }
}
